import { SystemPageType } from '@ravago/shared/page-data/enums/system-page-type';
import { UnitPrice } from '@ravago/shared/page-data/models/elements';
import { QuotationLine, QuotationStatus } from '@ravago/shared/page-data/models/features/product-quotation.model';
import { Badge } from '@ravago/shared/radiance/components/badge/components/Badge/Badge';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { BreakpointSwitch } from '@ravago/shared/radiance/components/breakpoint-switch/components/BreakpointSwitch/BreakpointSwitch';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { Paper } from '@ravago/shared/radiance/components/paper/components/Paper/Paper';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

import { usePathLocale } from '../../../../hooks/use-path-locale';
import { SystemPageLink } from '../../system-page-link/system-page-link';

interface Props {
   quotationLine: QuotationLine;
   quoteDetailBaseRoute: string | null;
   packaging: string | undefined;
   isOverview?: boolean;
   showShoppingCartBtn: boolean;
   hasOrderingPermission: boolean;
   showOfflinePrices: boolean;
   isAddingToCart: boolean;
   onAddToCart: () => void;
}

const QuoteCardItem = ({ label, value, highlight = false }: { label: string; value: string | undefined; highlight?: boolean }) => (
   <Box gap="sm">
      <Typography variant="small" color="subtle">
         {label}
      </Typography>
      <Typography variant={highlight ? 'small-highlight' : 'small'}>{value ?? '-'}</Typography>
   </Box>
);

const StatusBadge = ({ status }: { status: QuotationStatus }) => {
   const translate = useTranslations();

   if (status === 'ACTIVE') return <Badge variant="success">{translate('prices.quote-active')}</Badge>;
   return <Badge variant="error">{translate('prices.quote-expired')}</Badge>;
};

const AddToCartButton = ({
   isDisabled,
   hasOrderingPermission,
   fullWidth = false,
   onClick
}: {
   isDisabled: boolean;
   hasOrderingPermission: boolean;
   fullWidth?: boolean;
   onClick: () => void;
}) => {
   const translate = useTranslations();

   return (
      <Box direction="column" gap="xs">
         <Button leadingIcon="shopping-cart" fullWidth={fullWidth} disabled={isDisabled} onClick={isDisabled ? undefined : onClick}>
            {translate('prices.add-to-cart')}
         </Button>
         {!hasOrderingPermission && (
            <Typography variant="small" color="subtle">
               {translate('permissions.no-place-orders-permission')}
            </Typography>
         )}
      </Box>
   );
};

const getUnitPriceDifference = (offlineUnitPrice: UnitPrice, onlineUnitPrice: UnitPrice): UnitPrice => ({
   unitOfMeasure: offlineUnitPrice.unitOfMeasure,
   price: {
      amount: offlineUnitPrice.price.amount - onlineUnitPrice.price.amount,
      currency: offlineUnitPrice.price.currency
   }
});

interface PriceSectionProps {
   quotationLine: QuotationLine;
   showOfflinePrices: boolean;
}

const PriceSection = ({ quotationLine, showOfflinePrices }: PriceSectionProps) => {
   const translate = useTranslations();
   const format = useFormatter();

   if (showOfflinePrices && quotationLine.offlineUnitPrice) {
      return (
         <Box direction="column" items="end" gap="3xs">
            <Typography variant="small" color="primary">
               {translate('prices.save-amount', {
                  amount: format.unitPrice(getUnitPriceDifference(quotationLine.offlineUnitPrice, quotationLine.unitPrice))
               })}
            </Typography>
            <Typography variant="body-highlight" color="primary">
               {format.unitPrice(quotationLine.unitPrice)}
               {quotationLine.isPriceFixed === false && '*'}
            </Typography>
            <Typography variant="small" color="primary" lineThrough>
               {format.unitPrice(quotationLine.offlineUnitPrice)}
               {quotationLine.isPriceFixed === false && '*'}
            </Typography>
            {quotationLine.isPriceFixed === false && (
               <Typography variant="caption" color="subtle">
                  *{translate('commodity-pricing.to-be-confirmed')}
               </Typography>
            )}
         </Box>
      );
   }

   return (
      <Box direction="column" items="end" gap="3xs">
         <Typography variant="small" color="subtle">
            {translate('prices.unit-price')}
         </Typography>
         <Typography variant="body-highlight" color="primary">
            {format.unitPrice(quotationLine.unitPrice)}
            {quotationLine.isPriceFixed === false && '*'}
         </Typography>
         {quotationLine.isPriceFixed === false && (
            <Typography variant="caption" color="subtle">
               *{translate('commodity-pricing.to-be-confirmed')}
            </Typography>
         )}
      </Box>
   );
};

export const ProductPriceCardQuotationLine = ({
   quotationLine,
   quoteDetailBaseRoute,
   packaging,
   isOverview,
   showShoppingCartBtn,
   hasOrderingPermission,
   showOfflinePrices,
   isAddingToCart,
   onAddToCart
}: Props) => {
   const locale = usePathLocale();
   const translate = useTranslations();
   const format = useFormatter();

   const isButtonDisabled =
      isAddingToCart || !showShoppingCartBtn || (showShoppingCartBtn && (!hasOrderingPermission || quotationLine.quotation.status === 'EXPIRED'));

   return (
      <Paper>
         <Box direction="column" gap="xs">
            <Box justify="between" items="start">
               <Box gap="xs">
                  {isOverview ? (
                     <SystemPageLink
                        baseRoute={quoteDetailBaseRoute}
                        locale={locale}
                        type={SystemPageType.QUOTE_DETAIL}
                        slug={quotationLine.quotation.id}
                        underline="always"
                        fullWidth={false}
                     >
                        <Typography variant="body-highlight" color="inherit">
                           {quotationLine.quotation.name}
                        </Typography>
                     </SystemPageLink>
                  ) : (
                     <Typography variant="body-highlight" color="primary">
                        {format.quantity(quotationLine.quantity)}
                     </Typography>
                  )}
                  <StatusBadge status={quotationLine.quotation.status} />
               </Box>
               <Box items="start" gap="md">
                  <PriceSection quotationLine={quotationLine} showOfflinePrices={showOfflinePrices} />
                  {showShoppingCartBtn && (
                     <BreakpointSwitch excludeViews={['tab-port', 'phone']}>
                        <AddToCartButton isDisabled={isButtonDisabled} hasOrderingPermission={hasOrderingPermission} onClick={onAddToCart} />
                     </BreakpointSwitch>
                  )}
               </Box>
            </Box>

            <Box direction="column" gap="sm">
               {isOverview && <QuoteCardItem label={translate('prices.quantity')} value={format.quantity(quotationLine.quantity)} />}
               <QuoteCardItem label={translate('prices.packaging')} value={packaging} />
               <Box direction="column" gap="2xs">
                  <Box gap="sm">
                     <Typography variant="small" color="subtle">
                        {translate('prices.total')}
                     </Typography>
                     <Typography variant="small-highlight">
                        {format.price(quotationLine.totalPrice)}
                        {quotationLine.isPriceFixed === false && '*'}
                     </Typography>
                  </Box>
                  {quotationLine.isPriceFixed === false && (
                     <Typography variant="caption" color="subtle">
                        *
                        {quotationLine.marketPricingMonth
                           ? translate('commodity-pricing.price-contingent-to-month-index', {
                                month: format.fullMonthName(quotationLine.marketPricingMonth)
                             })
                           : translate('commodity-pricing.to-be-confirmed')}
                     </Typography>
                  )}
               </Box>
               {quotationLine.remark && <QuoteCardItem label={translate('prices.remark')} value={quotationLine.remark} />}
            </Box>

            {showShoppingCartBtn && (
               <BreakpointSwitch includeViews={['tab-port', 'phone']}>
                  <AddToCartButton isDisabled={isButtonDisabled} hasOrderingPermission={hasOrderingPermission} fullWidth onClick={onAddToCart} />
               </BreakpointSwitch>
            )}
         </Box>
      </Paper>
   );
};
